import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'

interface Crumb {
  label: string
  href?: string
}

/**
 * Route-based breadcrumb segments.
 * Keeps labels compact and auto-generated from routes.
 */
const ROUTE_LABELS: Record<string, string> = {
  '': 'Dashboard',
  'runs': 'Runs',
  'new': 'New',
  'sessions': 'Sessions',
  'orchestrate': 'Orchestrate',
  'projects': 'Projects',
  'service-catalog': 'Services',
  'assistant': 'Assistant',
  'stats': 'Statistics',
  'tokens': 'Token Usage',
  'adoption': 'Adoption',
  'configuration': 'Configuration',
  'model-pricing': 'Model Pricing',
  'users': 'Users',
  'prompts': 'Prompts',
  'budget': 'Budget',
  'edit': 'Edit',
  'error': 'Error',
}

function buildCrumbs(pathname: string): Crumb[] {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return [{ label: 'Dashboard' }]

  const crumbs: Crumb[] = [{ label: 'Dashboard', href: '/' }]
  let path = ''

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    path += '/' + seg
    const label = ROUTE_LABELS[seg]
    const isLast = i === segments.length - 1

    if (label) {
      crumbs.push({ label, href: isLast ? undefined : path })
    } else {
      // Dynamic segment (ID) — truncate
      const shortId = seg.length > 12 ? seg.slice(0, 8) + '…' : seg
      crumbs.push({ label: shortId, href: isLast ? undefined : path })
    }
  }

  return crumbs
}

/**
 * Auto-generated breadcrumbs from the current route.
 * Shows: Dashboard > Sessions > sess_abc123
 */
export function Breadcrumbs() {
  const location = useLocation()
  const crumbs = buildCrumbs(location.pathname)

  if (crumbs.length <= 1) return null

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/50" />}
          {crumb.href ? (
            <Link
              to={crumb.href}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {i === 0 ? <Home className="h-3.5 w-3.5" /> : crumb.label}
            </Link>
          ) : (
            <span className="text-foreground font-medium">{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}
